import { jsPDF } from 'jspdf';
import Plotly from 'plotly.js-basic-dist-min';
import { computeDotPlotData } from './dotPlotData';
import { computeStripData } from './stripPlotData';
import {
  STRIP_GROUP_LABELS, GROUP_MARKERS, STRIP_GROUP_ORDER, GROUP_SYMBOLS,
  IMMGEN_COLORS,
} from './colors';

const GENES_PER_PAGE = 35;
const STRIPS_PER_PAGE = 12; // 4 cols x 3 rows
const STRIP_COLS = 4;
const STRIP_ROWS = 3;
const nGroups = STRIP_GROUP_ORDER.length;

/**
 * Compute separator positions and group headers for PDF dot plot.
 */
function getPdfDecorations(nCols, nGenes, visibleIndices) {
  const groupBoundaries = [
    { label: "ImmGen (Yoshida '19)", origIndices: [0, 1, 2, 3] },
    { label: 'A20', origIndices: [4, 5] },
    { label: 'EmBO Genotypes', origIndices: [6, 7, 8] },
    { label: 'EmBO Transformed', origIndices: [9, 10, 11] },
  ];

  const shapes = [];
  const annotations = [];
  const origToVisible = {};
  if (visibleIndices) {
    visibleIndices.forEach((orig, vis) => { origToVisible[orig] = vis; });
  } else {
    // All visible — identity mapping
    for (let i = 0; i < nCols; i++) origToVisible[i] = i;
  }

  let prevGroupEnd = -1;
  for (const group of groupBoundaries) {
    const visibleInGroup = group.origIndices
      .filter(i => origToVisible[i] !== undefined)
      .map(i => origToVisible[i]);

    if (visibleInGroup.length === 0) continue;
    const groupStart = Math.min(...visibleInGroup);
    const groupEnd = Math.max(...visibleInGroup);

    if (prevGroupEnd >= 0 && groupStart > prevGroupEnd) {
      const sepX = (prevGroupEnd + groupStart) / 2;
      shapes.push({
        type: 'line',
        x0: sepX, x1: sepX,
        y0: -0.5, y1: nGenes - 0.5,
        line: { color: '#333', width: 1.5 },
        xref: 'x', yref: 'y',
      });
    }

    const centerX = (groupStart + groupEnd) / 2;
    annotations.push({
      x: centerX, y: -1.5,
      text: group.label,
      showarrow: false,
      font: { size: 11, color: '#777' },
      xref: 'x', yref: 'y',
    });

    prevGroupEnd = groupEnd;
  }

  return { separatorShapes: shapes, groupAnnotations: annotations };
}

/**
 * Render a dot plot for a chunk of genes offscreen, return as base64 PNG.
 */
async function renderChunkImage(genes, expression, colLabels, colorMode, visibleIndices) {
  const plotData = computeDotPlotData({ genes, expression, colLabels, colorMode });
  const nGenes = genes.length;
  const nCols = colLabels.length;
  const plotHeight = Math.max(400, nGenes * 28 + 150);
  const plotWidth = Math.max(700, 400 + nCols * 50);

  const { separatorShapes, groupAnnotations } = getPdfDecorations(nCols, nGenes, visibleIndices);

  const missingShapes = plotData.missingRows.map(i => ({
    type: 'rect',
    x0: -0.5, x1: nCols - 0.5,
    y0: i - 0.4, y1: i + 0.4,
    fillcolor: '#f0f0f0',
    line: { color: '#ccc', width: 0.5, dash: 'dot' },
    xref: 'x', yref: 'y',
  }));

  const missingAnnotations = plotData.missingRows.map(i => ({
    x: nCols / 2, y: i,
    text: 'no murine ortholog',
    showarrow: false,
    font: { size: 10, color: '#999' },
    xref: 'x', yref: 'y',
  }));

  const sizeLegendTraces = plotData.sizeRefVals.map(v => {
    const markerSize = Math.max(4, (v / plotData.maxLog) * 30);
    return {
      type: 'scatter',
      mode: 'markers',
      x: [null], y: [null],
      marker: { size: markerSize, color: '#ccc', line: { width: 0.5, color: '#999' } },
      name: `${v.toFixed(0)}`,
      showlegend: true,
      legendgroup: 'size',
      hoverinfo: 'skip',
    };
  });

  const traces = [
    {
      type: 'scatter',
      mode: 'markers',
      x: plotData.x,
      y: plotData.y,
      marker: {
        size: plotData.sizes,
        color: plotData.colors,
        colorscale: plotData.colorscale,
        cmin: plotData.cmin,
        cmax: plotData.cmax,
        colorbar: {
          title: { text: plotData.colorbarTitle, font: { size: 11, color: '#333' } },
          tickfont: { size: 10, color: '#555' },
          len: 0.4,
          thickness: 12,
          x: 1.06,
          outlinewidth: 0,
        },
        line: { width: 0.5, color: '#999' },
      },
      text: plotData.hoverTexts,
      hoverinfo: 'text',
      showlegend: false,
    },
    ...sizeLegendTraces,
  ];

  const layout = {
    width: plotWidth,
    height: plotHeight,
    paper_bgcolor: '#ffffff',
    plot_bgcolor: '#ffffff',
    margin: { l: 120, r: 80, t: 60, b: 30 },
    xaxis: {
      tickvals: [...Array(nCols).keys()],
      ticktext: colLabels,
      side: 'top',
      tickfont: { size: 10, color: '#333' },
      range: [-0.8, nCols],
      gridcolor: '#eee',
      zeroline: false,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    yaxis: {
      tickvals: [...Array(nGenes).keys()],
      ticktext: genes,
      autorange: 'reversed',
      tickfont: { size: 10, color: '#333', family: 'Inter' },
      gridcolor: '#eee',
      zeroline: false,
      showline: true,
      linecolor: '#ccc',
      linewidth: 1,
    },
    shapes: [...separatorShapes, ...missingShapes],
    annotations: [...missingAnnotations, ...groupAnnotations],
    hovermode: false,
    legend: {
      title: { text: 'log₂(expr+1)', font: { size: 10, color: '#555' } },
      x: 1.02, y: 0.15,
      font: { size: 9, color: '#555' },
      bgcolor: 'rgba(255,255,255,0)',
      borderwidth: 0,
      tracegroupgap: 0,
      itemsizing: 'constant',
    },
  };

  // Create offscreen container
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    await Plotly.newPlot(container, traces, layout, { displayModeBar: false });
    const dataUrl = await Plotly.toImage(container, {
      format: 'png',
      width: plotWidth,
      height: plotHeight,
      scale: 2,
    });
    return { dataUrl, width: plotWidth, height: plotHeight };
  } finally {
    Plotly.purge(container);
    document.body.removeChild(container);
  }
}

/**
 * Render a single strip plot gene offscreen, return as base64 PNG.
 */
async function renderSingleStripImage(gene, perSample, expression) {
  const result = computeStripData({ gene, perSample, expression9col: expression });
  if (!result) return null;

  const { traces, shapes, yRange } = result;
  const plotW = 250;
  const plotH = 220;

  const layout = {
    width: plotW,
    height: plotH,
    paper_bgcolor: '#ffffff',
    plot_bgcolor: '#ffffff',
    margin: { l: 36, r: 10, t: 28, b: 46 },
    title: {
      text: gene,
      font: { size: 11, color: '#1a1a1a', family: 'Inter' },
      y: 0.96,
    },
    xaxis: {
      tickvals: [...Array(nGroups).keys()],
      ticktext: STRIP_GROUP_LABELS.map(l => l.replace('\n', ' ')),
      tickfont: { size: 6, color: '#555' },
      range: [-0.5, nGroups - 0.5],
      zeroline: false,
      showline: true,
      linecolor: '#ccc',
    },
    yaxis: {
      title: { text: 'log₂(TPM+1)', font: { size: 7, color: '#777' } },
      tickfont: { size: 7, color: '#555' },
      range: yRange,
      gridcolor: '#eee',
      zeroline: false,
      showline: true,
      linecolor: '#ccc',
    },
    shapes,
    hovermode: false,
  };

  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.top = '-9999px';
  document.body.appendChild(container);

  try {
    await Plotly.newPlot(container, traces, layout, { displayModeBar: false });
    const dataUrl = await Plotly.toImage(container, {
      format: 'png',
      width: plotW,
      height: plotH,
      scale: 2,
    });
    return dataUrl;
  } finally {
    Plotly.purge(container);
    document.body.removeChild(container);
  }
}

/**
 * Render a grid of strip plot images for one page (up to 12 genes).
 * Returns array of { dataUrl, col, row }.
 */
async function renderStripGridImages(geneChunk, perSample, expression) {
  const results = [];
  for (let i = 0; i < geneChunk.length; i++) {
    const col = i % STRIP_COLS;
    const row = Math.floor(i / STRIP_COLS);
    const dataUrl = await renderSingleStripImage(geneChunk[i], perSample, expression);
    if (dataUrl) {
      results.push({ dataUrl, col, row });
    }
  }
  return results;
}

/**
 * Draw a strip plot legend on the PDF page.
 */
function drawStripLegend(doc, x, y) {
  doc.setFontSize(7);

  // Sample markers legend
  let cx = x;
  doc.setTextColor(100, 100, 100);
  doc.text('Samples:', cx, y);
  cx += 16;
  for (const g of STRIP_GROUP_ORDER) {
    const m = GROUP_MARKERS[g];
    doc.setTextColor(
      parseInt(m.color.slice(1, 3), 16),
      parseInt(m.color.slice(3, 5), 16),
      parseInt(m.color.slice(5, 7), 16)
    );
    doc.text(GROUP_SYMBOLS[g], cx, y);
    cx += 3;
    doc.setTextColor(80, 80, 80);
    doc.text(m.name, cx, y);
    cx += doc.getTextWidth(m.name) + 3;
  }

  // ImmGen ref legend on next line
  cx = x;
  const y2 = y + 4;
  doc.setTextColor(100, 100, 100);
  doc.text('ImmGen ref:', cx, y2);
  cx += 19;
  for (const [label, color] of Object.entries(IMMGEN_COLORS)) {
    doc.setDrawColor(
      parseInt(color.slice(1, 3), 16),
      parseInt(color.slice(3, 5), 16),
      parseInt(color.slice(5, 7), 16)
    );
    doc.setLineDashPattern([1, 1], 0);
    doc.line(cx, y2 - 1, cx + 6, y2 - 1);
    cx += 7;
    doc.setTextColor(80, 80, 80);
    doc.text(`${label} mean`, cx, y2);
    cx += doc.getTextWidth(`${label} mean`) + 4;
  }
  doc.setLineDashPattern([], 0); // reset
}

/**
 * Generate a multi-page PDF report from a gene list.
 */
export async function generatePDF({ genes, expression, perSample, colLabels, colorMode, visibleIndices }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 12;
  const colorLabel = colorMode === 'relative' ? 'Z-score' : 'Absolute';
  const nCols = colLabels.length;
  const dateStr = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const footer = "Data: ImmGen (Yoshida '19), GEO GSE203162, GSE173114, GSE255548 | Murine B-Cell Expression Explorer v0.1";

  // --- Dot plot pages ---
  const dotChunks = [];
  for (let i = 0; i < genes.length; i += GENES_PER_PAGE) {
    dotChunks.push(genes.slice(i, i + GENES_PER_PAGE));
  }

  for (let i = 0; i < dotChunks.length; i++) {
    if (i > 0) doc.addPage();

    // Header
    doc.setFontSize(14);
    doc.setTextColor(26, 26, 26);
    doc.text('Murine B-Cell Expression — Dot Plot Report', margin, margin + 4);

    // Subtitle
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    const subtitle = `${genes.length} genes | ${nCols} conditions | ${colorLabel} | ${dateStr}` +
      (dotChunks.length > 1 ? ` | Page ${i + 1}/${dotChunks.length}` : '');
    doc.text(subtitle, margin, margin + 10);

    // Render plot image
    const { dataUrl, width, height } = await renderChunkImage(
      dotChunks[i], expression, colLabels, colorMode, visibleIndices
    );

    // Fit image to page
    const availW = pageW - margin * 2;
    const availH = pageH - margin * 2 - 20;
    const scale = Math.min(availW / (width * 0.264583), availH / (height * 0.264583));
    const imgW = width * 0.264583 * scale;
    const imgH = height * 0.264583 * scale;
    const imgX = margin + (availW - imgW) / 2;

    doc.addImage(dataUrl, 'PNG', imgX, margin + 14, imgW, imgH);

    // Footer
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(footer, margin, pageH - 5);
  }

  // --- Strip plot pages ---
  if (perSample) {
    const stripChunks = [];
    for (let i = 0; i < genes.length; i += STRIPS_PER_PAGE) {
      stripChunks.push(genes.slice(i, i + STRIPS_PER_PAGE));
    }

    const gridStartY = margin + 18;
    const cellW = 66;
    const cellH = 56;

    for (let p = 0; p < stripChunks.length; p++) {
      doc.addPage();

      // Header
      doc.setFontSize(14);
      doc.setTextColor(26, 26, 26);
      doc.text('Murine B-Cell Expression — Strip Plots', margin, margin + 4);

      // Subtitle
      doc.setFontSize(9);
      doc.setTextColor(120, 120, 120);
      const stripSub = `Individual Sample Expression | ${genes.length} genes | ${dateStr}` +
        (stripChunks.length > 1 ? ` | Page ${p + 1}/${stripChunks.length}` : '');
      doc.text(stripSub, margin, margin + 10);

      // Render strip images for this chunk
      const gridImages = await renderStripGridImages(stripChunks[p], perSample, expression);

      // Tile images onto page
      const gridOffsetX = margin + (pageW - margin * 2 - STRIP_COLS * cellW) / 2;
      for (const { dataUrl, col, row } of gridImages) {
        const x = gridOffsetX + col * cellW;
        const y = gridStartY + row * cellH;
        doc.addImage(dataUrl, 'PNG', x, y, cellW, cellH);
      }

      // Legend below grid
      const legendY = gridStartY + STRIP_ROWS * cellH + 4;
      drawStripLegend(doc, margin, legendY);

      // Footer
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(footer, margin, pageH - 5);
    }
  }

  // --- Gene list page (if many genes) ---
  if (genes.length > 30) {
    doc.addPage();
    doc.setFontSize(12);
    doc.setTextColor(26, 26, 26);
    doc.text('Gene List', margin, margin + 4);
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);

    const geneText = genes.join(', ');
    const lines = doc.splitTextToSize(geneText, pageW - margin * 2);
    doc.text(lines, margin, margin + 12);

    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text(`${genes.length} genes | Generated ${dateStr}`, margin, pageH - 5);
  }

  doc.save(`expression_report_${genes.length}genes.pdf`);
}
